import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Lock, Envelope, CalendarBlank, ShieldCheck, CircleNotch, CheckCircle, ArrowLeft, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [step, setStep] = useState(1); // 1: verify identity, 2: reset password
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [secretAnswer, setSecretAnswer] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email || !dob || !secretAnswer) { toast.error('All fields are required'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, date_of_birth: dob, secret_answer: secretAnswer }),
      });
      const data = await res.json();
      if (res.ok) {
        setResetToken(data.reset_token);
        setStep(2);
        toast.success('Identity verified!');
      } else {
        toast.error(data.detail || 'Verification failed');
      }
    } catch { toast.error('Connection error'); }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset_token: resetToken, new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) { setDone(true); toast.success('Password reset successfully!'); }
      else { toast.error(data.detail || 'Reset failed'); }
    } catch { toast.error('Connection error'); }
    setLoading(false);
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 p-4">
        <div className="w-full max-w-md bg-white dark:bg-zinc-900 border rounded-2xl p-8 text-center shadow-lg">
          <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={28} className="text-emerald-600" weight="fill" />
          </div>
          <h2 className="text-xl font-bold mb-2">Password Reset Successful</h2>
          <p className="text-sm text-muted-foreground mb-6">You can now log in with your new password.</p>
          <Button onClick={() => navigate('/login')} className="w-full" data-testid="go-to-login">Go to Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 p-4">
      <div className="w-full max-w-md bg-white dark:bg-zinc-900 border rounded-2xl p-8 shadow-lg" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={14} /> Back to Login
        </Link>

        {step === 1 ? (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                <ShieldCheck size={24} className="text-blue-600" weight="duotone" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Forgot Password</h1>
                <p className="text-sm text-muted-foreground">Verify your identity to reset your password</p>
              </div>
            </div>

            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <div className="relative">
                  <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="pl-9" required data-testid="recovery-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <div className="relative">
                  <CalendarBlank size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input type="date" value={dob} onChange={e => setDob(e.target.value)} className="pl-9" required data-testid="recovery-dob" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Security Question Answer</Label>
                <Input value={secretAnswer} onChange={e => setSecretAnswer(e.target.value)} placeholder="Your answer (case insensitive)" required data-testid="recovery-answer" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="verify-identity-btn">
                {loading ? <CircleNotch size={16} className="animate-spin mr-2" /> : <ShieldCheck size={16} className="mr-2" />}
                {loading ? 'Verifying...' : 'Verify Identity'}
              </Button>
            </form>

            <div className="mt-6 p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground flex items-start gap-2">
                <WarningCircle size={14} className="shrink-0 mt-0.5" />
                If you haven't set up your security profile, contact your company admin or email <a href="mailto:info@sgkapps.com" className="text-primary underline">info@sgkapps.com</a> for assistance.
              </p>
            </div>

            <div className="text-center mt-4">
              <Link to="/forgot-username" className="text-sm text-primary hover:underline">Forgot your email/username?</Link>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                <Lock size={24} className="text-emerald-600" weight="duotone" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Set New Password</h1>
                <p className="text-sm text-muted-foreground">Your identity has been verified</p>
              </div>
            </div>

            <form onSubmit={handleReset} className="space-y-4">
              <div className="space-y-2">
                <Label>New Password</Label>
                <PasswordInput value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 8 characters" required data-testid="new-password" />
              </div>
              <div className="space-y-2">
                <Label>Confirm Password</Label>
                <PasswordInput value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter your password" required data-testid="confirm-password" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-btn">
                {loading ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Lock size={16} className="mr-2" />}
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export function ForgotUsernamePage() {
  const [firstName, setFirstName] = useState('');
  const [dob, setDob] = useState('');
  const [secretAnswer, setSecretAnswer] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleRecover = async (e) => {
    e.preventDefault();
    if (!firstName || !dob || !secretAnswer) { toast.error('All fields are required'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-username`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: firstName, date_of_birth: dob, secret_answer: secretAnswer }),
      });
      const data = await res.json();
      if (res.ok) { setResult(data); }
      else { toast.error(data.detail || 'Account not found'); }
    } catch { toast.error('Connection error'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 p-4">
      <div className="w-full max-w-md bg-white dark:bg-zinc-900 border rounded-2xl p-8 shadow-lg" data-testid="forgot-username-page">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={14} /> Back to Login
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
            <Envelope size={24} className="text-amber-600" weight="duotone" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Forgot Username</h1>
            <p className="text-sm text-muted-foreground">Recover your email/username</p>
          </div>
        </div>

        {result ? (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle size={28} className="text-emerald-600" weight="fill" />
            </div>
            <p className="font-medium">Account Found!</p>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Your email address is:</p>
              <p className="text-lg font-mono font-bold" data-testid="recovered-email">{result.email}</p>
            </div>
            <Button onClick={() => window.location.href = '/login'} className="w-full" data-testid="go-to-login-btn">Go to Login</Button>
          </div>
        ) : (
          <form onSubmit={handleRecover} className="space-y-4">
            <div className="space-y-2">
              <Label>First Name</Label>
              <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Your first name" required data-testid="recover-first-name" />
            </div>
            <div className="space-y-2">
              <Label>Date of Birth</Label>
              <Input type="date" value={dob} onChange={e => setDob(e.target.value)} required data-testid="recover-dob" />
            </div>
            <div className="space-y-2">
              <Label>Security Question Answer</Label>
              <Input value={secretAnswer} onChange={e => setSecretAnswer(e.target.value)} placeholder="Your answer (case insensitive)" required data-testid="recover-answer" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="recover-username-btn">
              {loading ? <CircleNotch size={16} className="animate-spin mr-2" /> : null}
              {loading ? 'Searching...' : 'Recover Username'}
            </Button>

            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground flex items-start gap-2">
                <WarningCircle size={14} className="shrink-0 mt-0.5" />
                If recovery fails, contact your company admin or email <a href="mailto:info@sgkapps.com" className="text-primary underline">info@sgkapps.com</a> for assistance.
              </p>
            </div>

            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-primary hover:underline">Forgot your password instead?</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
